import type {
  App6EchelonCode,
  App6Entry,
  App6Palette,
  CompositionCandidate,
  CompositionSide,
  CompositionWrite,
  UnitRole,
} from "../../api/types";
import { fold } from "../orbats/app6";
import { echelonFromSidc } from "../orbats/sidc";
import { roleShort } from "./roles";
import type { SideView } from "./rows";
import { t } from "../../i18n/i18n";

/** Fenêtre « Ajouter une unité » (docs/18 §6.4) : fonctions pures, testées par addUnitModel.test.ts. */

export type AddOrigin = "app6" | "same" | "catalogue" | "odb";

/** Rang des échelons APP-6, de l'équipe à la division (« 00 », sans échelon : hors rang). */
const RANK: Partial<Record<App6EchelonCode, number>> = {
  "11": 1,
  "12": 2,
  "13": 3,
  "14": 4,
  "15": 5,
  "16": 6,
  "17": 7,
  "18": 8,
  "21": 9,
};

export function echelonRank(code: string | null | undefined): number | null {
  return (code && RANK[code as App6EchelonCode]) || null;
}

/** Échelon APP-6 d'un échelon moteur (« BATAILLON » → « 16 »). */
const ENGINE_ECHELON: Record<string, App6EchelonCode> = {
  EQUIPE: "11",
  GROUPE: "12",
  SECTION: "14",
  COMPAGNIE: "15",
  BATAILLON: "16",
  REGIMENT: "17",
  BRIGADE: "18",
  DIVISION: "21",
};

/** Échelon d'une formation ennemie d'après son code iPad (« 1(TI)DIVBLIND » → division), faute de symbole. */
export function echelonFromEniCode(code: string | null | undefined): App6EchelonCode | null {
  const suffix = (code ?? "").replace(/^[^)]*\)/, "").toUpperCase();
  if (suffix.startsWith("DIV")) return "21";
  if (suffix.startsWith("BRIG")) return "18";
  if (suffix.startsWith("BAT") || suffix.startsWith("GTIA")) return "16";
  if (suffix.startsWith("CIE") || suffix.startsWith("ESC")) return "15";
  if (suffix.startsWith("SON") || suffix.startsWith("SEC")) return "14";
  return null;
}

/** Échelon APP-6 d'une unité de la liste : lu sur son symbole, sinon sur son échelon moteur. */
export function unitEchelon(unit: { sidc?: string | null; echelon?: string | null }): App6EchelonCode | null {
  const fromSidc = unit.sidc && /^\d{20}$/.test(unit.sidc) ? echelonFromSidc(unit.sidc) : null;
  return (fromSidc && echelonRank(fromSidc) !== null ? fromSidc : null) ?? ENGINE_ECHELON[unit.echelon ?? ""] ?? null;
}

/** Échelon de la racine d'un camp (force commandée, formation ennemie), lu sur son symbole ; à défaut, sur le code de
 *  la formation ennemie. */
export function rootEchelon(view: SideView | null | undefined): App6EchelonCode | null {
  const root = view?.out.units.find((u) => u.depth === 0);
  if (root) return echelonFromSidc(root.sidc);
  if (view?.root?.sidc) return echelonFromSidc(view.root.sidc);
  return echelonFromEniCode(view?.root?.label);
}

/** Échelons proposés pour une unité de la palette : ceux du camp, strictement inférieurs à la racine (tous, racine
 *  inconnue), du plus élevé au plus bas. `allowed` (ennemi : échelons que le serveur admet face au niveau du dossier,
 *  `composition.eni.echelons`) : seulement ceux-là — face à un régiment, pas de brigade ennemie. */
export function admittedEchelons(
  palette: App6Palette,
  side: CompositionSide,
  root: App6EchelonCode | null,
  allowed?: readonly string[] | null,
): App6Palette["echelons"] {
  const top = echelonRank(root);
  return palette.echelons
    .filter((e) => e.sides.includes(side) && echelonRank(e.code) !== null && (top === null || echelonRank(e.code)! < top))
    .filter((e) => !allowed || allowed.includes(e.code))
    .sort((a, b) => echelonRank(b.code)! - echelonRank(a.code)!);
}

/** Échelon proposé d'emblée : celui juste sous la racine (le plus élevé admis). */
export function defaultEchelon(echelons: App6Palette["echelons"]): App6EchelonCode | null {
  return echelons[0]?.code ?? null;
}

/** L'échelon d'une unité candidate est-il admis (strictement sous la racine) ? */
export function echelonFits(code: string | null | undefined, root: App6EchelonCode | null): boolean {
  const rank = echelonRank(code);
  const top = echelonRank(root);
  return rank === null || top === null || rank < top;
}

/** Candidats filtrés par la recherche (libellé, code, ordre de bataille, spécialité, sans accents ni casse). */
export function filterCandidates(items: readonly CompositionCandidate[], query: string): CompositionCandidate[] {
  const q = fold(query.trim());
  if (!q) return [...items];
  return items.filter((c) =>
    fold(`${c.label} ${c.code} ${c.odb_name} ${c.parent_label ?? ""} ${c.specialty ?? ""}`).includes(q),
  );
}

/** Onglet ouvert d'emblée : « Même ordre de bataille » s'il propose des unités, sinon le catalogue. */
export function openingOrigin(sameCount: number | null, catalogue = true): AddOrigin {
  // Sans catalogue (côté ennemi face à l'ennemi générique, registre B39) : la palette APP-6.
  return sameCount === 0 ? (catalogue ? "catalogue" : "app6") : "same";
}

/** Détail d'une ligne candidate : échelon, spécialité, formation de rattachement dans son ordre de bataille, ordre de
 *  bataille (lignes homonymes distinguées). */
export function candidateDetail(c: CompositionCandidate, echelon: string | null, specialty: string | null): string {
  return [echelon, specialty, c.parent_label && c.parent_label !== c.odb_name ? c.parent_label : null, c.odb_name]
    .filter(Boolean)
    .join(" · ");
}

/** Rôle par défaut d'une unité de la palette : celui de la palette côté ennemi ; côté ami, automatique pour une unité
 *  de mêlée, sinon commandement, soutien ou appuis selon son arme. */
export function paletteDefaultRole(entry: App6Entry, side: CompositionSide): UnitRole | null {
  if (side === "ENI") return entry.eni_role ?? (entry.manoeuvre ? "ech1" : "appuis");
  if (entry.manoeuvre) return null;
  if (entry.arm === "COMMANDEMENT" || entry.arm === "TRANSMISSIONS") return "commandement";
  if (["LOGISTIQUE", "TRAIN", "RAVITAILLEMENT", "MAINTENANCE", "SANTE"].includes(entry.arm)) return "soutien";
  return "appuis";
}

/** Effet attendu d'un ajout, en clair (volet de confirmation) ; `company` : rôles du S/GTIA (docs/21 §4). */
export function fitsSentence(fits: CompositionCandidate["fits"], role: UnitRole | null, company = false): string {
  switch (fits) {
    case "slot":
      return role
        ? t("Prendra l'emplacement libre de {role}", { role: roleShort(role, company).toLowerCase() })
        : t("Prendra un emplacement libre de la manœuvre");
    case "support":
      return t("Placée en appui de la brigade");
    case "attached":
      return t("Rattachée à la force, sans emplacement de la manœuvre");
    default:
      return t("Sera à placer sur la carte");
  }
}

/** Effet attendu d'un ajout de la palette (le serveur ne l'a pas encore calculé). Ennemi : la règle du serveur
 *  (`composition.eni.slot_rules`) — un emplacement libre seulement si l'échelon et le rôle de l'unité sont ceux qu'il
 *  admet, sinon à placer. Ami : emplacement libre de la manœuvre s'il en reste un et que le rôle en tient un,
 *  commandement rattaché, sinon à placer. */
export function paletteFits(
  view: SideView | null | undefined,
  role: UnitRole | null,
  echelon: string | null = null,
): CompositionCandidate["fits"] {
  if (view?.side === "ENI") {
    const rules = view.out.slot_rules ?? [];
    return rules.some((r) => r.echelon === echelon && role !== null && r.roles.includes(role)) ? "slot" : "unplaced";
  }
  if (role === "commandement") return "attached";
  const manoeuvre = role === null || role === "reco" || role === "ech1" || role === "ech2" || role === "renforts";
  if (manoeuvre && (view?.out.empty_slots.length ?? 0) > 0) return "slot";
  return "unplaced";
}

/** Effet attendu d'une unité candidate : celui du serveur pour son rôle par défaut ; pour un autre rôle choisi dans
 *  la fenêtre, estimé comme le fait le serveur (`composition._fits`) : appui ou soutien en appui de la brigade
 *  (ailleurs à placer), commandement rattaché sans emplacement, rôle de manœuvre sur un emplacement libre s'il en reste,
 *  sinon à placer. */
export function candidateFits(
  candidate: CompositionCandidate,
  view: SideView | null | undefined,
  role: UnitRole | null,
  level: string | null | undefined,
): CompositionCandidate["fits"] {
  if (role === candidate.role_default) return candidate.fits;
  if (role === "commandement") return "unplaced";
  if (role === "appuis" || role === "soutien") return view?.side === "AMI" && level === "BRIGADE" ? "support" : "unplaced";
  if (view?.side === "ENI") return paletteFits(view, role, candidate.echelon_code);
  if (candidate.fits === "slot") return "slot";
  return paletteFits(view, role);
}

/** Numéro saisi (T ou M) : chiffres seulement, huit au plus ; vide → null ; invalide → undefined. */
export function cleanNumber(text: string): string | null | undefined {
  const value = text.trim();
  if (!value) return null;
  return /^\d{1,8}$/.test(value) ? value : undefined;
}

/** Corps de l'ajout d'une unité candidate (même ordre de bataille, catalogue, ordres de bataille construits). */
export function candidateWrite(
  side: CompositionSide,
  candidate: CompositionCandidate,
  role: UnitRole | null,
  parent: string | null,
): CompositionWrite {
  return {
    op: "add",
    side,
    source: candidate.source,
    parent,
    role: role === candidate.role_default ? null : role,
  };
}

/** Corps de l'ajout d'une unité de la palette APP-6. */
export function paletteWrite(input: {
  side: CompositionSide;
  entry: App6Entry;
  app6: string;
  echelon: App6EchelonCode;
  role: UnitRole | null;
  parent: string | null;
  name: string;
  number: string | null;
  higher: string | null;
}): CompositionWrite {
  const fallback = paletteDefaultRole(input.entry, input.side);
  return {
    op: "add",
    side: input.side,
    source: { kind: "app6", app6: input.app6, fn: input.entry.key, echelon: input.echelon },
    parent: input.parent,
    role: input.role === fallback ? null : input.role,
    name: input.name.trim() ? input.name.trim().slice(0, 80) : null,
    number: input.number,
    higher: input.higher,
  };
}

/** Formations possibles pour le rattachement : la racine (« la force »), puis les unités de la force sous elle
 *  (deux niveaux, unités ajoutées exclues) d'un échelon strictement supérieur à celui de l'unité ajoutée (`below`,
 *  inconnu : toutes), dans l'ordre de l'arbre. */
export function parentChoices(
  view: SideView | null | undefined,
  below: App6EchelonCode | null = null,
): { id: string | null; label: string; depth: number }[] {
  if (!view) return [];
  const root = view.out.units.find((u) => u.depth === 0);
  const rootLabel = root?.label ?? view.root?.label;
  const out: { id: string | null; label: string; depth: number }[] = rootLabel ? [{ id: null, label: rootLabel, depth: 0 }] : [];
  const floor = echelonRank(below);
  for (const unit of view.out.units) {
    if (unit.depth < 1 || unit.depth > 2 || unit.origin === "added") continue;
    const rank = echelonRank(unitEchelon(unit));
    if (floor !== null && rank !== null && rank <= floor) continue;
    out.push({ id: unit.id, label: unit.label, depth: unit.depth });
  }
  return out;
}
